<template>
  <iColumn width="fill" gap="standard" data-test="saved-posts">
    <iText v-if="editMode" variant="subtle" data-test="saved-posts-editor-hint">
      The reader's saved posts appear here: All posts first, then each collection they made.
    </iText>
    <template v-else-if="canSavePosts">
      <iText v-if="!isUserLoggedIn" variant="subtle" data-test="saved-posts-sign-in">
        Sign in to see the posts you saved.
      </iText>
      <iText v-else-if="readerCollectionsError" variant="error" data-test="saved-posts-error">
        {{ readerCollectionsError }}
      </iText>
      <iLoadingSpinner v-else-if="!readerCollectionsLoaded" size="small" />
      <template v-else>
        <iRow gap="small" width="fill" data-test="saved-posts-collections">
          <iButton
            v-for="collection in collectionTabs"
            :key="collection.collectionId"
            size="small"
            :variant="collection.collectionId === selectedCollectionId ? 'primary' : 'secondary'"
            :aria-pressed="collection.collectionId === selectedCollectionId"
            :data-selected="collection.collectionId === selectedCollectionId"
            data-test="saved-posts-collection"
            :data-collection-id="collection.collectionId"
            @click="selectCollection(collection.collectionId)"
          >
            {{ collection.name }}
          </iButton>
        </iRow>
        <SmartFeed
          v-if="selectedCollectionId"
          :key="selectedCollectionId"
          :source="useCollectionItemFeedStore"
          :default-feed-layout="getOptionValue(SmartFeedLayout)"
          :default-entity-size="getOptionValue(SmartFeedCardLayout)"
          :edit-mode="editMode"
        >
          <template #item="{feedItem}">
            <StudioFeedRowItem
              v-if="rowTemplateFor(feedItem.type).componentId > 0"
              :entity-id="feedItem.id"
              :dynamic-component-id="rowTemplateFor(feedItem.type).componentId"
              :context-name="rowTemplateFor(feedItem.type).contextName"
              :theme-mode="themeMode"
              :edit-mode="editMode"
            />
          </template>
          <template #empty-state>
            <iText variant="subtle" data-test="saved-posts-empty">
              {{ getOptionValue(EmptyStateText) }}
            </iText>
          </template>
        </SmartFeed>
      </template>
    </template>
  </iColumn>
</template>

<script>
import { mapActions, mapState } from "pinia";
import SmartFeed from "SmartFeed/components/SmartFeedV2.vue";
import StudioFeedRowItem from "%/dynamic-component-editor/standard-elements/StudioFeedRowItem.vue";
import useFanPublicationStore from "%/dynamic-component-editor/stores/fan-publication-store";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import { SmartFeedElementMixin } from "%/dynamic-component-editor/mixins/smart-feed-element-mixin";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import EmptyStateText from "%/dynamic-component-editor/options/empty-state-text";
import { useCollectionItemFeedStore } from "SmartFeed/stores/feed/collection-item-feed-store";
import { collectionRowTemplate } from "%/dynamic-component-editor/helpers/collection-row-templates";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { useStoryStore } from "%/dynamic-component-editor/stores/story-store";
import { useSavedPostsStore } from "%/dynamic-component-editor/stores/saved-posts-store";

export default {
  name: "SavedPosts",
  components: {
    SmartFeed,
    StudioFeedRowItem,
  },
  mixins: [
    DynamicElementMixin,
    SmartFeedElementMixin,
  ],
  elementPanelSettings: {
    displayName: "Saved Posts",
    displayIcon: "material-symbols:bookmark-outline-rounded",
    elementGroup: "general",
    elementType: "element",
  },
  options: [
    new LayoutWidth({
      "default": "fill",
      "hover": "fill",
    }, true),
    new EmptyStateText({ "default": "Nothing saved here yet." }),
  ],
  data() {
    return { selectedCollectionId: 0 };
  },
  computed: {
    ...mapState(useConfigurationStore, ["canSavePosts"]),
    ...mapState(useStoryStore, ["isUserLoggedIn"]),
    ...mapState(useSavedPostsStore, ["savedCollections", "readerCollectionsLoaded", "readerCollectionsError"]),
    collectionTabs() {
      const { allPosts, named } = this.savedCollections;
      return allPosts ? [allPosts, ...named] : named;
    },
    destinationId() {
      return useFanPublicationStore().publicationId || Number(window?.blclient?.data?.themeDestinationId ?? 0) || 0;
    },
  },
  watch: {
    collectionTabs: {
      immediate: true,
      handler(tabs) {
        if (!tabs.some(tab => tab.collectionId === this.selectedCollectionId) && tabs.length > 0) {
          this.selectCollection(tabs[0].collectionId);
        }
      },
    },
  },
  created() {
    if (!this.editMode && this.canSavePosts && this.isUserLoggedIn) {
      this.loadReaderCollections();
    }
  },
  methods: {
    ...mapActions(useSavedPostsStore, ["loadReaderCollections"]),
    useCollectionItemFeedStore,
    selectCollection(collectionId) {
      const feed = useCollectionItemFeedStore();
      feed.$reset();
      feed.collectionId = collectionId;
      this.selectedCollectionId = collectionId;
    },
    rowTemplateFor(entityType) {
      return collectionRowTemplate(entityType, this.getOptionValue(this.SmartFeedCardLayout), this.destinationId);
    },
  },
};
</script>
